import noop from "lodash/noop";

import { chosenSites } from "app/common/services/chosenSites";
import { MediaPreview } from "app/components/media-preview/MediaPreview";
import { EllipsisText } from "app/components/React/ellipsis-text/EllipsisText";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { AdsGalleryData } from "../hooks/useAdsGallery";

import { ModalDetails } from "./ModalDetails";
import { ModalImage } from "./ModalImage";
import { ModalTable } from "./ModalTable";
import {
    Label,
    ContentGrid,
    Divider,
    Header,
    HeaderInfo,
    QueryBarItemSingleStyled,
    ScrollContainer,
    CampaignName,
    ExitButton,
    QueryBarItemDivider,
} from "./StyledComponents";

type AdModalContentProps = AdsGalleryData & {
    onClose: () => void;
    controller?: AdIntelligenceController;
    isOverviewContext?: boolean;
    industryName?: string;
};

export const AdModalContent = ({
    onClose,
    controller = "advertiser",
    isOverviewContext = false,
    industryName,
    ...adData
}: AdModalContentProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const primarySite = chosenSites.getPrimarySite();
    const selectedSite = params.adsSelectedSite || primarySite?.name;
    const selectedSiteFavicon = getFaviconUrl(selectedSite);

    // Industry ads carry no publisher, and the analyzed site is the publisher only on
    // the publisher pages — everywhere else the ad's own advertiser stands on its own.
    const isIndustryContext = Boolean(industryName);
    const adAdvertiser = "advertiser" in adData ? adData.advertiser : null;
    const advertiser = isIndustryContext ? adAdvertiser : (adAdvertiser ?? selectedSite);
    const adAdvertiserFavicon = "advertiserFavicon" in adData ? adData.advertiserFavicon : null;
    const advertiserFavicon = isIndustryContext ? adAdvertiserFavicon : (adAdvertiserFavicon ?? selectedSiteFavicon);
    const showPublisher = !isIndustryContext && controller === "publisher" && Boolean(adAdvertiser);
    const publisher = showPublisher ? selectedSite : null;
    const publisherFavicon = showPublisher
        ? (("publisherImageUri" in adData ? adData.publisherImageUri : null) ?? selectedSiteFavicon)
        : null;
    // Publishers are resolved per creative under an advertiser key: the ad's own
    // advertiser in industry context, the analyzed site everywhere else.
    const publishersKey = isIndustryContext ? adAdvertiser : selectedSite;
    const isVideo = adData.creativeType === "video";
    const sourceKey = `${adData.creativeImageUri}-${adData.videoUrl}`;

    return (
        <MediaPreview.Provider key={sourceKey}>
            <Header data-automation="ad-modal-header">
                <HeaderInfo>
                    {publisher ? (
                        <>
                            <Label>ad.intelligence.advertiser.ad.modal.publisher.label</Label>
                            <QueryBarItemSingleStyled
                                text={publisher}
                                image={publisherFavicon}
                                onItemClick={noop}
                                renderIconComponent={() => null}
                            />
                            <QueryBarItemDivider />
                        </>
                    ) : null}
                    {isIndustryContext ? (
                        <>
                            <Label>ad.intelligence.advertiser.ad.modal.industry.label</Label>
                            <CampaignName>{industryName}</CampaignName>
                            {advertiser ? <QueryBarItemDivider /> : null}
                        </>
                    ) : null}
                    {advertiser && advertiserFavicon ? (
                        <>
                            <Label>ad.intelligence.advertiser.ad.modal.advertiser.label</Label>
                            <QueryBarItemSingleStyled
                                text={advertiser}
                                image={advertiserFavicon}
                                onItemClick={noop}
                                renderIconComponent={() => null}
                            />
                        </>
                    ) : null}
                    {adData.landingPagesLinks?.length <= 1 && adData.campaignTitle ? (
                        <>
                            <Label>ad.intelligence.advertiser.ad.modal.advertiser.campaign.label</Label>
                            <EllipsisText
                                text={adData.campaignTitle}
                                ItemTagContainer={CampaignName}
                                placement="bottom-center"
                            />
                        </>
                    ) : null}
                </HeaderInfo>
                <ExitButton
                    iconName="clear"
                    type="flat"
                    onClick={onClose}
                />
            </Header>
            <Divider />
            <ContentGrid data-automation="ad-modal-body">
                <ModalImage {...adData} />
                <ScrollContainer>
                    <ModalDetails
                        {...adData}
                        controller={controller}
                        isOverviewContext={isOverviewContext}
                    />
                    {controller === "advertiser" && publishersKey ? (
                        <ModalTable
                            creativeId={adData.creativeId}
                            isVideo={isVideo}
                            isOverviewContext={isOverviewContext}
                            advertiserDomain={isIndustryContext ? publishersKey : undefined}
                        />
                    ) : null}
                </ScrollContainer>
            </ContentGrid>
        </MediaPreview.Provider>
    );
};
